(() => {
  const mounted = new WeakSet();
  const mountInformation = (root) => {
    const drawer = root.querySelector('[data-info-dialog]');
    if (!drawer) return () => {};
    const title = drawer.querySelector('[data-info-title]');
    const topics = [...drawer.querySelectorAll('[data-info-topic]')];
    const panels = [...drawer.querySelectorAll('[data-info-panel]')];
    const content = drawer.querySelector('[data-info-content]');
    const controller = new AbortController();
    const listen = (element, type, handler) => element.addEventListener(type, handler, { signal: controller.signal });
    let opener;
    let bodyState;
    let savedScroll = 0;
    const selectTopic = (topic) => {
      const selected = topics.find((button) => button.dataset.infoTopic === topic);
      if (!selected) return false;
      topics.forEach((button) => button.setAttribute('aria-pressed', String(button === selected)));
      panels.forEach((panel) => { panel.hidden = panel.dataset.infoPanel !== topic; });
      title.textContent = selected.dataset.topicTitle;
      content.scrollTop = 0;
      return true;
    };
    const restorePage = () => {
      if (!bodyState) return;
      Object.assign(document.body.style, bodyState);
      bodyState = undefined;
      window.scrollTo({ top: savedScroll, left: 0, behavior: 'instant' });
      if (opener?.isConnected) opener.focus({ preventScroll: true });
      opener = undefined;
    };
    const close = () => {
      if (drawer.open) drawer.close();
      restorePage();
    };
    root.querySelectorAll('[data-info-open]').forEach((button) => listen(button, 'click', () => {
      if (!selectTopic(button.dataset.infoOpen)) return;
      opener = button;
      if (!drawer.open) {
        savedScroll = window.scrollY;
        bodyState = { position: document.body.style.position, top: document.body.style.top, width: document.body.style.width, overflow: document.body.style.overflow };
        drawer.showModal();
        Object.assign(document.body.style, { position: 'fixed', top: `-${savedScroll}px`, width: '100%', overflow: 'hidden' });
      }
      title.focus({ preventScroll: true });
    }));
    topics.forEach((button) => listen(button, 'click', () => selectTopic(button.dataset.infoTopic)));
    drawer.querySelectorAll('[data-info-close]').forEach((button) => listen(button, 'click', close));
    listen(drawer, 'cancel', (event) => { event.preventDefault(); close(); });
    listen(drawer, 'close', () => { if (!drawer.open) restorePage(); });
    listen(drawer, 'keydown', (event) => {
      if (event.key === 'Escape') { event.preventDefault(); close(); return; }
      if (event.key !== 'Tab') return;
      const focusables = [...drawer.querySelectorAll('button:not([disabled]),a[href],input:not([disabled]),select:not([disabled]),textarea:not([disabled]),summary,[tabindex]')].filter((element) => element.tabIndex >= 0 && !element.closest('[hidden]'));
      if (!focusables.length) { event.preventDefault(); title.focus(); return; }
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      if (!focusables.includes(document.activeElement)) { event.preventDefault(); (event.shiftKey ? last : first).focus(); }
      else if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
      else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
    });
    listen(drawer, 'click', (event) => {
      if (event.target !== drawer) return;
      const bounds = drawer.getBoundingClientRect();
      if (event.clientX < bounds.left || event.clientX > bounds.right || event.clientY < bounds.top || event.clientY > bounds.bottom) close();
    });
    return () => { close(); controller.abort(); };
  };
  const mountFinalUpload = (root, focusPersonalization) => {
    const dialog = root.querySelector('[data-final-dialog]');
    if (!dialog) return () => {};
    const title = dialog.querySelector('[data-final-title]');
    const yes = dialog.querySelector('[data-final-yes]');
    const no = dialog.querySelector('[data-final-no]');
    const area = dialog.querySelector('[data-final-upload-area]');
    const input = area.querySelector('[data-photo-input]');
    const controller = new AbortController();
    const listen = (element, type, handler) => element.addEventListener(type, handler, { signal: controller.signal });
    let opener;
    let bodyState;
    let savedScroll = 0;
    let returnToPersonalization = false;
    const restorePage = () => {
      if (!bodyState) return;
      Object.assign(document.body.style, bodyState);
      bodyState = undefined;
      window.scrollTo({ top: savedScroll, left: 0, behavior: 'instant' });
      if (returnToPersonalization) focusPersonalization();
      else if (opener?.isConnected) opener.focus({ preventScroll: true });
      opener = undefined;
      returnToPersonalization = false;
    };
    const close = (personalize = false) => {
      returnToPersonalization = personalize;
      if (dialog.open) dialog.close();
      restorePage();
    };
    root.querySelectorAll('[data-final-open]').forEach((button) => listen(button, 'click', () => {
      opener = button;
      if (!dialog.open) {
        savedScroll = window.scrollY;
        bodyState = { position: document.body.style.position, top: document.body.style.top, width: document.body.style.width, overflow: document.body.style.overflow };
        dialog.showModal();
        Object.assign(document.body.style, { position: 'fixed', top: `-${savedScroll}px`, width: '100%', overflow: 'hidden' });
      }
      title.focus({ preventScroll: true });
    }));
    listen(yes, 'click', () => {
      yes.setAttribute('aria-pressed', 'true');
      no.setAttribute('aria-pressed', 'false');
      area.hidden = false;
      input.focus({ preventScroll: true });
    });
    listen(no, 'click', () => {
      yes.setAttribute('aria-pressed', 'false');
      no.setAttribute('aria-pressed', 'true');
      area.hidden = true;
      // Hiding the final branch keeps its selected File and local preview intact.
      close(true);
    });
    dialog.querySelectorAll('[data-final-close]').forEach((button) => listen(button, 'click', () => close()));
    listen(dialog, 'cancel', (event) => { event.preventDefault(); close(); });
    listen(dialog, 'close', () => { if (!dialog.open) restorePage(); });
    listen(dialog, 'keydown', (event) => {
      if (event.key === 'Escape') { event.preventDefault(); close(); return; }
      if (event.key !== 'Tab') return;
      const focusables = [...dialog.querySelectorAll('button:not([disabled]),a[href],input:not([disabled]),select:not([disabled]),textarea:not([disabled]),[tabindex]')].filter((element) => element.tabIndex >= 0 && !element.closest('[hidden]'));
      if (!focusables.length) { event.preventDefault(); title.focus(); return; }
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      if (!focusables.includes(document.activeElement)) { event.preventDefault(); (event.shiftKey ? last : first).focus(); }
      else if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
      else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
    });
    listen(dialog, 'click', (event) => {
      if (event.target !== dialog) return;
      const bounds = dialog.getBoundingClientRect();
      if (event.clientX < bounds.left || event.clientX > bounds.right || event.clientY < bounds.top || event.clientY > bounds.bottom) close();
    });
    return () => { close(); controller.abort(); };
  };
  const mountPhotoSlot = (root, panel, slot) => {
    const input = slot.querySelector('[data-photo-input]');
    const preview = slot.querySelector('[data-photo-preview]');
    const image = slot.querySelector('[data-photo-image]');
    const fileName = slot.querySelector('[data-photo-name]');
    const error = slot.querySelector('[data-photo-error]');
    const remove = slot.querySelector('[data-photo-remove]');
    const finalSlot = slot.dataset.photoSlot === 'final';
    const summary = root.querySelector('[data-final-summary]');
    const summaryImage = root.querySelector('[data-final-design-summary]');
    const status = root.querySelector('[data-final-design-status]');
    const controller = new AbortController();
    const listen = (element, type, handler) => element.addEventListener(type, handler, { signal: controller.signal });
    let objectUrl;
    let pendingImage;
    let selectionVersion = 0;
    const release = () => {
      selectionVersion += 1;
      if (pendingImage) { pendingImage.onload = null; pendingImage.onerror = null; }
      pendingImage = undefined;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
      objectUrl = undefined;
    };
    const clearFinalSummary = () => {
      if (!finalSlot) return;
      summaryImage.removeAttribute('src');
      summary.hidden = true;
      status.textContent = status.dataset.empty;
    };
    const reset = () => {
      release();
      input.value = '';
      image.removeAttribute('src');
      preview.hidden = true;
      fileName.textContent = '';
      error.textContent = '';
      error.hidden = true;
      input.removeAttribute('aria-invalid');
      clearFinalSummary();
    };
    const showError = (message) => {
      reset();
      error.textContent = message;
      error.hidden = false;
      input.setAttribute('aria-invalid', 'true');
    };
    const selectPhoto = () => {
      const files = input.files;
      if (!files?.length) return;
      if (files.length !== 1) { showError(panel.dataset.errorCount); return; }
      const file = files[0];
      if (!['image/jpeg', 'image/png', 'image/webp'].includes(file.type)) { showError(panel.dataset.errorType); return; }
      if (file.size > 12 * 1024 * 1024) { showError(panel.dataset.errorSize); return; }
      release();
      clearFinalSummary();
      error.textContent = '';
      error.hidden = true;
      input.removeAttribute('aria-invalid');
      preview.hidden = true;
      image.removeAttribute('src');
      fileName.textContent = '';
      objectUrl = URL.createObjectURL(file);
      const selectedUrl = objectUrl;
      const version = selectionVersion;
      const candidate = new Image();
      pendingImage = candidate;
      candidate.onload = () => {
        if (version !== selectionVersion || objectUrl !== selectedUrl) return;
        pendingImage = undefined;
        candidate.onload = null;
        candidate.onerror = null;
        image.src = selectedUrl;
        preview.hidden = false;
        fileName.textContent = file.name;
        if (finalSlot) {
          summaryImage.src = selectedUrl;
          summary.hidden = false;
          status.textContent = `${status.dataset.selected} ${file.name}`;
        }
      };
      candidate.onerror = () => {
        if (version === selectionVersion && objectUrl === selectedUrl) showError(panel.dataset.errorRead);
      };
      candidate.src = selectedUrl;
    };
    listen(input, 'change', selectPhoto);
    listen(remove, 'click', () => { reset(); input.focus({ preventScroll: true }); });
    return () => { release(); controller.abort(); };
  };
  const mount = (root) => {
    if (mounted.has(root)) return;
    mounted.add(root);
    const controller = new AbortController();
    const listen = (element, type, handler, capture = false) => element?.addEventListener(type, handler, { signal: controller.signal, capture });
    const galleryImage = root.querySelector('[data-gallery-image]');
    const summaryImage = root.querySelector('[data-summary-image]');
    const dialogImage = root.querySelector('[data-dialog-image]');
    const dialog = root.querySelector('[data-art-dialog]');
    const sampleButtons = [...root.querySelectorAll('[data-art-select]')];
    sampleButtons.forEach((button) => listen(button, 'click', () => {
      sampleButtons.forEach((item) => { item.classList.toggle('is-active', item === button); item.setAttribute('aria-pressed', String(item === button)); });
      [galleryImage, summaryImage, dialogImage].forEach((img) => { if (img) { img.src = button.dataset.image; img.alt = button.dataset.alt; } });
    }));
    listen(root.querySelector('[data-art-zoom]'), 'click', () => dialog?.showModal());
    listen(root.querySelector('[data-close-dialog]'), 'click', () => dialog?.close());
    listen(dialog, 'click', (event) => { if (event.target === dialog) { const r = dialog.getBoundingClientRect(); if (event.clientX < r.left || event.clientX > r.right || event.clientY < r.top || event.clientY > r.bottom) dialog.close(); } });
    const panel = root.querySelector('[data-customize-panel]');
    const steps = [...panel.querySelectorAll('[data-form-step]')];
    const progress = panel.querySelector('[data-form-progress]');
    const controls = panel.querySelector('[data-step-controls]');
    const back = panel.querySelector('[data-step-back]');
    const next = panel.querySelector('[data-step-next]');
    const final = panel.querySelector('[data-form-final]');
    const buy = root.querySelector('[data-form-submit]');
    // Local image previews are never proof of real artwork intake or payment readiness.
    if (buy) buy.disabled = true;
    let currentStep = 0;
    const goStep = (step, focus = true) => {
      currentStep = Math.max(0, Math.min(steps.length - 1, step));
      steps.forEach((item, index) => { item.hidden = index !== currentStep; });
      progress.querySelectorAll('[data-step-go]').forEach((item, index) => { if (index === currentStep) item.setAttribute('aria-current', 'step'); else item.removeAttribute('aria-current'); });
      back.hidden = currentStep === 0;
      next.hidden = currentStep === steps.length - 1;
      final.hidden = currentStep !== steps.length - 1;
      if (focus) { const target = steps[currentStep].querySelector('input,textarea,select'); target?.focus(); }
    };
    const revealInvalidStep = (event) => {
      const index = steps.findIndex((step) => step.contains(event.target));
      if (index !== -1) goStep(index);
    };
    listen(root, 'invalid', revealInvalidStep, true);
    if (steps.length && progress && controls && back && next && final) {
      progress.hidden = false;
      controls.hidden = false;
      progress.querySelectorAll('[data-step-go]').forEach((item) => listen(item, 'click', () => goStep(Number(item.dataset.stepGo) - 1)));
      listen(back, 'click', () => goStep(currentStep - 1));
      listen(next, 'click', () => goStep(currentStep + 1));
      goStep(0, false);
    }
    const focusPersonalization = () => {
      goStep(0, false);
      const heading = panel.querySelector('[data-personalization-title]');
      panel.scrollIntoView({ block: 'start', behavior: 'instant' });
      heading.focus({ preventScroll: true });
    };
    const disposers = [mountInformation(root), mountFinalUpload(root, focusPersonalization)];
    root.querySelectorAll('[data-photo-slot]').forEach((slot) => disposers.push(mountPhotoSlot(root, panel, slot)));
    listen(root, 'submit', (event) => event.preventDefault());
    const cleanup = () => {
      disposers.forEach((dispose) => dispose());
      controller.abort();
      document.removeEventListener('shopify:section:unload', sectionUnload);
      window.removeEventListener('pagehide', pageHide);
      mounted.delete(root);
    };
    const sectionUnload = (event) => { if (event.target.contains(root)) cleanup(); };
    const pageHide = (event) => { if (!event.persisted) cleanup(); };
    document.addEventListener('shopify:section:unload', sectionUnload);
    window.addEventListener('pagehide', pageHide);
  };
  const mountAll = (scope) => {
    if (scope.matches && scope.matches('[data-garage-section]')) mount(scope);
    scope.querySelectorAll('[data-garage-section]').forEach(mount);
  };
  mountAll(document);
  document.addEventListener('shopify:section:load', (event) => mountAll(event.target));
})();
